import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

function generatePassword() {
  const syllables = [
    'ab','ac','ad','af','ag','ah','ak','al','am','an','ap','ar','as','at','av','aw','ax','az',
    'ba','be','bi','bo','bu','ca','ce','ci','co','cu','da','de','di','do','du',
    'fa','fe','fi','fo','fu','ga','ge','gi','go','gu','ha','he','hi','ho','hu',
    'ja','je','ji','jo','ju','ka','ke','ki','ko','ku','la','le','li','lo','lu',
    'ma','me','mi','mo','mu','na','ne','ni','no','nu','pa','pe','pi','po','pu',
    'ra','re','ri','ro','ru','sa','se','si','so','su','ta','te','ti','to','tu',
    'va','ve','vi','vo','vu','wa','we','wi','wo','xu','ya','ye','yo','za','ze','zi','zo','zu',
  ];
  const specials = ['$', '!', '#', '%', '*', '+', ',', '-', '.', ';', '_', ':', '/'];
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const syllableCount = 4 + Math.floor(Math.random() * 5);
  let pw = '';
  for (let i = 0; i < syllableCount; i++) {
    const s = pick(syllables);
    pw += i % 2 === 1 ? cap(s) : s;
  }
  pw += pick(specials) + String(Math.floor(Math.random() * 900) + 100);
  return pw;
}

function generateEmail(firstName, lastName) {
  const first = (firstName || '').trim().toLowerCase().replace(/\s+.*$/, '').charAt(0);
  const last = (lastName || '').trim().toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z-]/g, '');
  if (!first || !last) return '';
  return `${first}.${last}@en-power.eu`;
}

const HEADER_STYLE = {
  padding: '22px 28px 18px',
  borderBottom: '1px solid #E2E8F0',
  background: '#fff',
};

const CARD_STYLE = {
  background: '#fff',
  border: '1px solid #E2E8F0',
  borderRadius: '14px',
  overflow: 'hidden',
};

const BTN_PRIMARY = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  padding: '10px 20px',
  border: 'none',
  borderRadius: '10px',
  background: '#122A5C',
  color: '#fff',
  fontSize: '13px',
  fontWeight: 700,
  fontFamily: 'Manrope, sans-serif',
  cursor: 'pointer',
  letterSpacing: '-0.01em',
};

const BTN_SECONDARY = {
  ...BTN_PRIMARY,
  background: '#F1F5F9',
  color: '#122A5C',
  border: '1px solid #D7DEE9',
};

const CHECKBOX_STYLE = {
  width: '18px',
  height: '18px',
  accentColor: '#E07000',
  cursor: 'pointer',
};

export default function MittwaldMailGenerator({ apiBase = '/api', session = null }) {
  const accessToken = session?.access_token || null;
  const isAuthenticated = Boolean(accessToken);

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [generated, setGenerated] = useState({});
  const [writing, setWriting] = useState(false);

  const authHeaders = useMemo(() => (
    accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
  ), [accessToken]);

  const loadDeals = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Bitte zuerst einloggen.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${apiBase}/contracts/usage`, {
        params: { op: 'vermittler-it-deals', _ts: Date.now() },
        headers: authHeaders,
      });
      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      setDeals(items);
      setSelectedIds(new Set());
      setGenerated({});
      if (items.length === 0) {
        toast.info('Keine Deals in der Phase IT-einrichten gefunden.');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Deals konnten nicht geladen werden.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeaders, isAuthenticated]);

  useEffect(() => {
    loadDeals();
  }, [loadDeals]);

  const toggleSelect = useCallback((dealId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(dealId)) {
        next.delete(dealId);
      } else {
        next.add(dealId);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (selectedIds.size === deals.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(deals.map((d) => d.dealId)));
    }
  }, [deals, selectedIds.size]);

  const handleGenerate = useCallback(() => {
    if (selectedIds.size === 0) {
      toast.warning('Bitte mindestens einen Deal auswählen.');
      return;
    }
    const result = {};
    for (const deal of deals) {
      if (!selectedIds.has(deal.dealId)) continue;
      const email = generateEmail(deal.firstName, deal.lastName);
      const password = generatePassword();
      result[deal.dealId] = { email, password };
    }
    setGenerated(result);
    toast.success(`${Object.keys(result).length} E-Mail(s) generiert.`);
  }, [deals, selectedIds]);

  const generatedEntries = useMemo(() => (
    deals.filter((d) => generated[d.dealId]).map((d) => ({
      ...d,
      ...generated[d.dealId],
    }))
  ), [deals, generated]);

  const handleDownloadCsv = useCallback(() => {
    if (generatedEntries.length === 0) {
      toast.warning('Erst generieren, dann downloaden.');
      return;
    }
    const header = 'E-Mail-Adresse;Postfachgroesse in GB;Passwort;Spamschutz';
    const rows = generatedEntries.map((e) => `${e.email};150;${e.password};ja`);
    const csv = [header, ...rows, ''].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Mittwald-Postfaecher-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('CSV heruntergeladen.');
  }, [generatedEntries]);

  const handleWriteToBitrix = useCallback(async () => {
    if (generatedEntries.length === 0) {
      toast.warning('Erst generieren, dann in Bitrix schreiben.');
      return;
    }
    setWriting(true);
    try {
      const updates = generatedEntries.map((e) => ({
        dealId: e.dealId,
        email: e.email,
        password: e.password,
      }));
      await axios.post(`${apiBase}/contracts/usage`, {
        action: 'vermittler-it-update',
        updates,
      }, { headers: authHeaders });
      toast.success(`${updates.length} Deal(s) in Bitrix aktualisiert.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Bitrix-Update fehlgeschlagen.');
    } finally {
      setWriting(false);
    }
  }, [apiBase, authHeaders, generatedEntries]);

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#F8FAFC', fontFamily: 'Manrope, sans-serif' }}>
      <div style={HEADER_STYLE}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '11px', fontWeight: 700, color: '#E07000', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>
              Vermittler IT-Einrichtung
            </div>
            <h2 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '22px', fontWeight: 700, color: '#122A5C', letterSpacing: '-0.03em' }}>
              Mittwald Postfächer
            </h2>
            <div style={{ color: '#64748B', fontSize: '13px', marginTop: '4px' }}>
              E-Mail-Adressen und Passwörter für neue Vertriebler generieren und als CSV exportieren.
            </div>
          </div>
          <button onClick={loadDeals} disabled={loading} style={BTN_SECONDARY}>
            {loading ? 'Lade…' : 'Aktualisieren'}
          </button>
        </div>
      </div>

      <div style={{ padding: '20px 28px 40px', maxWidth: '1100px' }}>
        {error && (
          <div style={{ padding: '14px 18px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', color: '#991B1B', fontSize: '13px', marginBottom: '18px' }}>
            {error}
          </div>
        )}

        {deals.length > 0 && (
          <div style={{ ...CARD_STYLE, marginBottom: '20px' }}>
            <div style={{ padding: '14px 18px', background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#122A5C' }}>
                Deals in Phase „IT-einrichten" ({deals.length})
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button onClick={toggleAll} style={BTN_SECONDARY}>
                  {selectedIds.size === deals.length ? 'Keine auswählen' : 'Alle auswählen'}
                </button>
                <button onClick={handleGenerate} disabled={selectedIds.size === 0} style={{ ...BTN_PRIMARY, opacity: selectedIds.size === 0 ? 0.5 : 1 }}>
                  Generieren ({selectedIds.size})
                </button>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #E2E8F0', width: '40px' }}> </th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #E2E8F0' }}>Deal</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #E2E8F0' }}>Kontakt</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #E2E8F0' }}>Generierte E-Mail</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #E2E8F0' }}>Passwort</th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map((deal) => {
                    const gen = generated[deal.dealId];
                    const isSelected = selectedIds.has(deal.dealId);
                    return (
                      <tr
                        key={deal.dealId}
                        onClick={() => toggleSelect(deal.dealId)}
                        style={{ cursor: 'pointer', background: isSelected ? '#FFF7ED' : '#fff', transition: 'background 0.15s' }}
                      >
                        <td style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9' }}>
                          <input type="checkbox" checked={isSelected} onChange={() => {}} style={CHECKBOX_STYLE} />
                        </td>
                        <td style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9', color: '#122A5C', fontWeight: 600 }}>
                          <div>{deal.dealTitle}</div>
                          <div style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 400 }}>ID {deal.dealId}</div>
                        </td>
                        <td style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9', color: '#334155' }}>
                          {deal.contactName || 'n/a'}
                        </td>
                        <td style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9' }}>
                          {gen ? (
                            <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#059669', fontWeight: 600 }}>{gen.email}</span>
                          ) : (
                            <span style={{ color: '#CBD5E1' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9' }}>
                          {gen ? (
                            <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#7C3AED', fontWeight: 600 }}>{gen.password}</span>
                          ) : (
                            <span style={{ color: '#CBD5E1' }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {generatedEntries.length > 0 && (
          <div style={{ ...CARD_STYLE }}>
            <div style={{ padding: '14px 18px', background: '#F0FDF4', borderBottom: '1px solid #BBF7D0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#166534' }}>
                {generatedEntries.length} Postfach/Postfächer bereit
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button onClick={handleDownloadCsv} style={BTN_PRIMARY}>
                  CSV herunterladen
                </button>
                <button onClick={handleWriteToBitrix} disabled={writing} style={{ ...BTN_PRIMARY, background: '#E07000' }}>
                  {writing ? 'Schreibe…' : 'In Bitrix eintragen'}
                </button>
              </div>
            </div>

            <div style={{ padding: '14px 18px' }}>
              <div style={{ fontFamily: 'monospace', fontSize: '12px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '12px 14px', overflowX: 'auto', whiteSpace: 'pre', lineHeight: 1.8, color: '#334155' }}>
                <span style={{ color: '#94A3B8' }}>E-Mail-Adresse;Postfachgroesse in GB;Passwort;Spamschutz</span>
                {generatedEntries.map((e) => (
                  <React.Fragment key={e.dealId}>
                    {'\n'}{e.email};150;{e.password};ja
                  </React.Fragment>
                ))}
              </div>
              <div style={{ marginTop: '10px', fontSize: '11px', color: '#94A3B8' }}>
                Bitrix-Felder: E-Mail → UF_CRM_1740652985438 · Passwort → UF_CRM_1781865132519
              </div>
            </div>
          </div>
        )}

        {!loading && deals.length === 0 && !error && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94A3B8', fontSize: '14px' }}>
            Keine Deals in der Vermittler-Pipeline / Phase „IT-einrichten" gefunden.
          </div>
        )}
      </div>
    </div>
  );
}
